import PropTypes from 'prop-types';
import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { API_S3_BASE_URL } from '../../apiConstant';
import { useUploadToS3 } from '../../utils/hooks/useUploadToS3';
import { Loader } from '../loader';
import Button from '../button';
import PreviewModal from './PreviewModal';

const DragDrop2 = forwardRef(
  (
    {
      url = `${API_S3_BASE_URL}/api/s3/upload`,
      type,
      id,
      clientId,
      format = '',
      sizeLimit = 524288000,
      onUpload,
      onDelete,
      className = '',
      parentClassName = '',
      onError,
      variant = 'normal',
      subtitle = 'Support for PDF, Images and Documents',
      showPreviews = true,
      multiple = true,
      validateFile,
    },
    ref
  ) => {
    const [dragging, setDragging] = useState(false);
    const [stagedFiles, setStagedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const uploadToS3 = useUploadToS3({ url, type, id, clientId, onError });
    const isUploading = uploadToS3.isPending;
    const isSmall = variant === 'small';

    // 🔹 Cleanup on unmount
    useEffect(() => {
      return () => {
        // Cleanup removed to fix state issues and objectUrlsRef error
      };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 🔹 Main upload logic
    const uploadFiles = async (files) => {
      if (!files?.length) return;

      if (validateFile) {
        for (const file of Array.from(files)) {
          const validationMessage = await validateFile(file);
          if (validationMessage) {
            window.triggerAlert({
              title: 'Error',
              type: 'error',
              message: validationMessage,
            });
            onError?.(new Error(validationMessage));
            return;
          }
        }
      }

      if (sizeLimit && Array.from(files).some((f) => f.size > sizeLimit)) {
        return window.triggerAlert({
          title: 'Error',
          type: 'error',
          message: 'One or more files exceed the size limit',
        });
      }

      // 🚨 Deferred mode (no id yet)
      if (!id) {
        const newStaged = Array.from(files).map((file) => {
          const previewUrl = file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : null;
          return {
            file,
            previewUrl,
            id: Math.random().toString(36).substr(2, 9),
          };
        });
        setStagedFiles((prev) => multiple ? [...prev, ...newStaged] : newStaged);
        onUpload?.(
          newStaged.map((s) => ({
            url: s.previewUrl || null,
            name: s.file.name,
            isStaged: true,
          }))
        );
        return;
      }

      // ✅ Immediate upload
      try {
        const urls = await uploadToS3.mutateAsync({
          files,
          type,
          id,
          clientId,
        });

        onUpload?.(urls);
      } catch (error) {
        window.triggerAlert({
          title: 'Upload Failed',
          type: 'error',
          message:
            error?.response?.data?.message ||
            error?.message ||
            'File upload failed. Please try again.',
        });
      }
    };

    // 🔹 Expose triggerUpload(finalId)
    useImperativeHandle(
      ref,
      () => ({
        triggerUpload: async (finalId) => {
          if (!stagedFiles.length) return [];

          try {
            const urls = await uploadToS3.mutateAsync({
              files: stagedFiles.map((s) => s.file),
              type,
              id: finalId,
              clientId,
            });

            setStagedFiles([]);
            return urls;
          } catch (error) {
            window.triggerAlert({
              title: 'Upload Failed',
              type: 'error',
              message:
                error?.response?.data?.message ||
                error?.message ||
                'File upload failed. Please try again.',
            });
            return [];
          }
        },

        clearStagedFiles: () => {
          setStagedFiles([]);
        },
        triggerDelete: () => {
          onDelete?.();
        },
        removeFileByUrl: (url) => {
          const handleRemoveFile = (fileId) => {
            const fileToRemove = stagedFiles.find((s) => s.id === fileId);
            if (fileToRemove) {
              setStagedFiles((prev) => prev.filter((s) => s.id !== fileId));
            }
          };
        },
      }),
      [stagedFiles, onDelete, type, uploadToS3]
    );

    // UI handlers
    const handleDrop = async (e) => {
      e.preventDefault();
      setDragging(false);
      await uploadFiles(Array.from(e.dataTransfer.files));
    };

    const handleFileChange = async (e) => {
      await uploadFiles(Array.from(e.target.files || []));
      e.target.value = '';
    };

    return (
      <div className={parentClassName}>
        <div
          className={`
            flex flex-col items-center justify-center w-full
            ${isSmall ? 'p-4 max-w-sm' : 'p-6'}
            rounded-xl border border-gray-200 shadow-sm transition
            ${dragging ? 'bg-orange-50 border-orange-400' : 'bg-white'}
            ${className}
          `}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <Loader />
          ) : (
            <>
              {/* Icon */}
              <div
                className={`${isSmall ? 'w-8 h-8' : 'w-10 h-10'} flex items-center justify-center bg-orange-100 rounded-lg mb-3}`}
              >
                {' '}
                <svg
                  width={isSmall ? '20' : '26'}
                  height={isSmall ? '20' : '26'}
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  {' '}
                  <path
                    d="M12 16V4M12 4L7 9M12 4L17 9M4 16V18C4 19.6569 5.34315 21 7 21H17C18.6569 21 20 19.6569 20 18V16"
                    stroke="#ff9900"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />{' '}
                </svg>{' '}
              </div>

              <h3
                className={`${
                  isSmall ? 'text-sm' : 'text-lg'
                } font-semibold text-gray-800 mb-1`}
              >
                Drop your files here
              </h3>

              <p
                className={`${
                  isSmall ? 'text-xs' : 'text-sm'
                } text-gray-500 mb-4 text-center`}
              >
                {subtitle}
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept={format || 'file/*'}
                multiple={multiple}
                hidden
                onChange={handleFileChange}
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`bg-[#ff9900] text-white font-medium shadow-sm
                ${isSmall ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'}
                rounded-lg`}
              >
                + Choose Files
              </button>

              {/* � Staged Files Preview */}
                {!id && showPreviews && stagedFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPreviewModalOpen(true)}
                    className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-all"
                  >
                    {/* Stacked thumbnails */}
                    <div className="flex -space-x-2">
                      {stagedFiles.slice(0, 3).map((staged, i) => (
                        <div
                          key={staged.id}
                          className="w-6 h-6 rounded-full border-2 border-white overflow-hidden bg-gray-100 flex items-center justify-center"
                          style={{ zIndex: 3 - i }}
                        >
                          {staged.previewUrl ? (
                            <img src={staged.previewUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                              <svg className="w-3 h-3 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                          )}
                        </div>
                      ))}
                    </div>
                    <span className="text-xs font-semibold text-orange-600">
                      {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} ready
                    </span>
                    <svg className="w-3.5 h-3.5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                )}

                <PreviewModal
                  previewModalOpen={previewModalOpen}
                  setPreviewModalOpen={setPreviewModalOpen}
                  stagedFiles={stagedFiles}
                  setStagedFiles={setStagedFiles}
                />

            </>
          )}
        </div>
      </div>
    );
  }
);

DragDrop2.displayName = 'DragDrop2';

DragDrop2.propTypes = {
  url: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.string.isRequired,

  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
  className: PropTypes.string,
  parentClassName: PropTypes.string,
  sizeLimit: PropTypes.number,
  variant: PropTypes.oneOf(['normal', 'small']),
  format: PropTypes.string,
  subtitle: PropTypes.string,
  showPreviews: PropTypes.bool,
  multiple: PropTypes.bool,
  onError: PropTypes.func,
  validateFile: PropTypes.func,
};

export default DragDrop2;
